import type { GameSessionAIClient, SessionClock, SessionIdGenerator, SessionLogger } from './types.js';
import { createWorkerAIClient } from '../workers/worker-ai-client.js';

export function createDefaultClock(): SessionClock {
  return {
    now() {
      return Date.now();
    },
  };
}

let testIdCounter = 0;

export function createDefaultIdGenerator(prefix = 'match'): SessionIdGenerator {
  return {
    nextId() {
      testIdCounter += 1;
      return `${prefix}-${testIdCounter}-${Date.now().toString(36)}`;
    },
  };
}

export function resetTestIdCounter(): void {
  testIdCounter = 0;
}

export function createConsoleLogger(scope: string): SessionLogger {
  const withScope = (level: 'debug' | 'info' | 'warn' | 'error') =>
    (message: string, context?: Record<string, unknown>) => {
      const payload = context ? [message, context] : [message];
      console[level](`[${scope}]`, ...payload);
    };

  return {
    debug: withScope('debug'),
    info: withScope('info'),
    warn: withScope('warn'),
    error: withScope('error'),
  };
}

export function createSilentLogger(): SessionLogger {
  return {
    debug() {},
    info() {},
    warn() {},
    error() {},
  };
}

export interface DefaultAIClientOptions {
  logger?: SessionLogger;
  /** Override profile-driven play pacing. */
  minPlayDelayMs?: number;
  /** Override bid/pass pacing; production keeps the 1000ms default. */
  minBidDelayMs?: number;
}

export function createDefaultAIClient(options: DefaultAIClientOptions = {}): GameSessionAIClient {
  return createWorkerAIClient({
    ...(options.logger ? { logger: options.logger } : {}),
    ...(options.minPlayDelayMs !== undefined ? { minPlayDelayMs: options.minPlayDelayMs } : {}),
    ...(options.minBidDelayMs !== undefined ? { minBidDelayMs: options.minBidDelayMs } : {}),
  });
}
